<template>
  <div @mouseenter="pauseTimer" @mouseleave="resumeTimer"
    class="pointer-events-auto relative w-full max-w-sm overflow-hidden rounded-2xl border border-white/10 bg-[#111114] shadow-2xl backdrop-blur-xl transition-all duration-300 hover:border-white/20"
    role="alert">
    <!-- Header -->
    <div class="flex items-center gap-3 px-5 py-4 border-b border-white/5 bg-[#111214]/95">
      <!-- Icon -->
      <div class="p-2.5 rounded-xl border" :class="typeStyles.iconWrapperClass">
        <component :is="typeStyles.icon" class="h-5 w-5" :class="typeStyles.iconColor" aria-hidden="true" />
      </div>

      <!-- Title -->
      <p class="text-sm font-bold text-white flex-1">
        {{ notification.title || typeStyles.defaultTitle }}
      </p>

      <!-- Close Button -->
      <button @click="close"
        class="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20">
        <span class="sr-only">Close</span>
        <X class="h-4 w-4" aria-hidden="true" />
      </button>
    </div>

    <!-- Message Body -->
    <div v-if="notification.message" class="px-5 py-4">
      <p class="text-sm text-gray-400 leading-relaxed">
        {{ notification.message }}
      </p>
    </div>

    <!-- Progress Bar -->
    <div v-if="duration > 0" class="absolute bottom-0 left-0 h-0.5 w-full bg-white/5">
      <div class="h-full transition-all duration-100 ease-linear" :class="typeStyles.progressColor"
        :style="{ width: `${progress}%` }" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, type PropType } from "vue";
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from "lucide-vue-next";
import type { Notification } from "../useNotifications";

const props = defineProps({
  notification: {
    type: Object as PropType<Notification>,
    required: true,
  },
  duration: {
    type: Number,
    default: 5000,
  },
});

const emit = defineEmits(["close"]);

const progress = ref(100);
let timerId: number | undefined;
let startTime: number;
let remaining: number = props.duration;

const typeStyles = computed(() => {
  switch (props.notification.type) {
    case "success":
      return {
        icon: CheckCircle2,
        iconColor: "text-green-400",
        iconWrapperClass: "bg-green-500/10 border-green-500/20",
        progressColor: "bg-green-500",
        defaultTitle: "Success",
      };
    case "error":
      return {
        icon: XCircle,
        iconColor: "text-red-400",
        iconWrapperClass: "bg-red-500/10 border-red-500/20",
        progressColor: "bg-red-500",
        defaultTitle: "Error",
      };
    case "warning":
      return {
        icon: AlertTriangle,
        iconColor: "text-amber-400",
        iconWrapperClass: "bg-amber-500/10 border-amber-500/20",
        progressColor: "bg-amber-500",
        defaultTitle: "Warning",
      };
    case "info":
    default:
      return {
        icon: Info,
        iconColor: "text-blue-400",
        iconWrapperClass: "bg-blue-500/10 border-blue-500/20",
        progressColor: "bg-blue-500",
        defaultTitle: "Information",
      };
  }
});

const close = () => emit("close", props.notification.id);

const startTimer = () => {
  if (props.duration <= 0) return;
  startTime = Date.now();
  timerId = window.setInterval(() => {
    const elapsed = Date.now() - startTime;
    progress.value = Math.max(0, ((remaining - elapsed) / props.duration) * 100);
    if (elapsed >= remaining) close();
  }, 16);
};

const pauseTimer = () => {
  if (!timerId) return;
  remaining -= Date.now() - startTime;
  window.clearInterval(timerId);
  timerId = undefined;
};

const resumeTimer = () => {
  if (remaining <= 0) return;
  startTimer();
};

onMounted(() => startTimer());
onUnmounted(() => { if (timerId) window.clearInterval(timerId); });
</script>